import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Users, UserPlus, Edit, Trash2, FolderTree } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * TeamNode - Recursive component for tree structure
 */
const TeamNode = ({ team, level = 0, onEdit, onDelete, onAddSubteam, onSelect, selectedTeamId, allTeams }) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const { user } = useAuth();

    // Permission check helper
    const checkPerm = (perm) => {
        if (!user || !user.permissions) return true; // Default to true if no permissions defined (admin assumption)
        const scope = user.permissions[perm];
        return scope && scope !== 'none' && scope !== false;
    };

    // Find children
    const children = allTeams.filter(t => t.parent_id === team.id);
    const hasChildren = children.length > 0;

    const isSelected = selectedTeamId === team.id;

    const handleSelect = (e) => {
        // Prevent selection when clicking actions or expand toggle
        e.stopPropagation();
        onSelect(team);
    };

    return (
        <div className="select-none">
            <div
                onClick={handleSelect}
                className={`
                    flex flex-col md:flex-row md:items-center justify-between p-3 rounded-xl 
                    cursor-pointer transition-all group
                    ${level > 0 ? 'ml-6 border-l-2 border-gray-100' : ''}
                    ${isSelected
                        ? 'bg-primary/5 border-primary/20 shadow-sm ring-1 ring-primary/20'
                        : 'hover:bg-gray-50 border-transparent hover:border-gray-200'}
                `}
            >
                <div className="flex items-center gap-3 w-full md:w-auto">
                    {/* Expand Toggle */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                        className={`p-1 rounded-lg hover:bg-gray-200 text-gray-400 ${hasChildren ? 'visible' : 'invisible'}`}
                    >
                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>

                    {/* Team Icon */}
                    <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-105 shrink-0"
                        style={{ backgroundColor: team.color || '#3b82f6' }}
                    >
                        <Users size={20} />
                    </div>

                    {/* Team Info */}
                    <div className="min-w-0">
                        <h4 className={`font-semibold truncate ${isSelected ? 'text-primary' : 'text-gray-800'}`}>
                            {team.team_name}
                        </h4>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            {team.leader_name && (
                                <span className="flex items-center gap-1 truncate max-w-[120px]">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                                    Lead: {team.leader_name}
                                </span>
                            )}
                            <span className="shrink-0">• {team.member_count || 0} members</span>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className={`
                    flex items-center gap-1 
                    mt-3 md:mt-0 
                    w-full md:w-auto 
                    justify-end md:justify-start
                    border-t border-gray-100 pt-2 md:border-0 md:pt-0
                    transition-opacity 
                    ${isSelected ? 'opacity-100' : 'opacity-100 md:opacity-0 md:group-hover:opacity-100'}
                `}>
                    {checkPerm('team.create') && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onAddSubteam(team);
                            }}
                            className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-green-600 tooltip shadow-sm border border-transparent hover:border-gray-200"
                            title="Add Sub-team"
                        >
                            <UserPlus size={16} />
                        </button>
                    )}
                    {checkPerm('team.edit') && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onEdit(team);
                            }}
                            className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-blue-600 shadow-sm border border-transparent hover:border-gray-200"
                            title="Edit Team"
                        >
                            <Edit size={16} />
                        </button>
                    )}
                    {checkPerm('team.delete') && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete(team);
                            }}
                            className="p-2 hover:bg-white rounded-lg text-gray-500 hover:text-red-600 shadow-sm border border-transparent hover:border-gray-200"
                            title="Delete Team"
                        >
                            <Trash2 size={16} />
                        </button>
                    )}
                </div>
            </div>

            {/* Children Recursive Render */}
            {isExpanded && hasChildren && (
                <div className="mt-1">
                    {children.map(child => (
                        <TeamNode
                            key={child.id}
                            team={child}
                            level={level + 1}
                            allTeams={allTeams}
                            onEdit={onEdit}
                            onDelete={onDelete}
                            onAddSubteam={onAddSubteam}
                            onSelect={onSelect}
                            selectedTeamId={selectedTeamId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const TeamHierarchy = ({ teams, onEdit, onDelete, onAddSubteam, onSelect, selectedTeamId }) => {
    // Filter root teams (those with no parent or parent not in list)
    const rootTeams = teams.filter(t => !t.parent_id || !teams.find(p => p.id === t.parent_id));

    if (teams.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center p-12 bg-white rounded-2xl border border-dashed border-gray-200">
                <FolderTree size={48} className="text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-800">No teams structure yet</h3>
                <p className="text-gray-500 text-center max-w-sm mt-2">
                    Create your first team to start building your organization hierarchy.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <h3 className="text-lg font-bold text-gray-800 mb-4 px-3 flex items-center gap-2">
                <FolderTree size={20} className="text-gray-400" />
                Organization Structure
            </h3>
            <div className="space-y-1">
                {rootTeams.map(team => (
                    <TeamNode
                        key={team.id}
                        team={team}
                        allTeams={teams}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onAddSubteam={onAddSubteam}
                        onSelect={onSelect}
                        selectedTeamId={selectedTeamId}
                    />
                ))}
            </div>
        </div>
    );
};

export default TeamHierarchy;
